import { FC, useState } from "react";
import cn from "classnames";
import { Fade } from "@mui/material";
import { MONTHS_LIST, WEEK_DAYS_LIST } from "@/constants/robotConfigLists";
import { useTranslate } from "@/hooks";
import { CustomButton, CustomMenuItem, CustomTextField } from "@/ui";
import { ICalendar } from "./Calendar.props";
import styles from "./Calendar.module.scss";

const Calendar: FC<ICalendar> = ({ isDisabled, isHaveRights = true, changeFlg, setChangeFlg, saveChangesHandler, initialHolidayList }) => {
	const [year, setYear] = useState<number>(new Date().getFullYear()); // год
	const [holidays, setHolidays] = useState<string[]>(initialHolidayList); // список выходных

	const translate = useTranslate(); // hook для перевода текста'

	// создание массива данных месяца
	const createMonthArray = (year: number, month: number): [string | number, string][][] => {
		const mon = month - 1; // корректировка месяцев (в JS идут от 0 до 11)
		const date = new Date(year, mon);
		const dataArray: [string | number, string][][] = [[]];

		// пробелы для первого ряда с понедельника до первого дня месяца (* * * 1  2  3  4)
		for (let i = 0; i < getDay(date); i++) {
			dataArray[0].push(["", ""]);
		}

		// заполнение ячеек календаря с датами
		while (date.getMonth() === mon) {
			dataArray[dataArray.length - 1].push([date.getDate(), formatDate(year, month, date.getDate())]);
			// вс, последний день - перевод строки
			if (getDay(date) % 7 === 6) dataArray.push([]);
			date.setDate(date.getDate() + 1);
		}

		// добить таблицу пустыми ячейками, если нужно (29 30 31 * * * *)
		if (getDay(date) !== 0) {
			for (let i = getDay(date); i < 7; i++) {
				dataArray[dataArray.length - 1].push(["", ""]);
			}
		}
		return dataArray;
	};

	// получение номера дня недели, от 0 (пн) до 6 (вс)
	const getDay = (date: Date): number => {
		let day = date.getDay();
		if (day === 0) day = 7; // сделать воскресенье (0) последним днем
		return day - 1;
	};

	// форматирование даты в YYYY-MM-DD
	const formatDate = (year: number, month: number, day: number): string => {
		const formatMonth = month < 10 ? "0" + month : month;
		const formatDay = day < 10 ? "0" + day : day;
		return `${year}-${formatMonth}-${formatDay}`;
	};

	// обработчик нажатия мыши на ячейку календаря
	const clickHandler = (date: string) => {
		if (date !== "") {
			if (holidays.includes(date)) setHolidays((prev) => prev.filter((data) => data !== date));
			else setHolidays((prev) => [...prev, date].sort());
			setChangeFlg(true); // ставим флаг о несохраненных данных
		}
	};

	// текущая дата в формате YYYY-MM-DD
	const getCurrentDate = (): string => {
		const date = new Date();
		return formatDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
	};

	const handleSaveChanges = () => {
		saveChangesHandler(holidays);
	};

	return (
		<Fade in={true} timeout={500}>
			<div className={styles.wrapper}>
				<div className={styles.top}>
					{/* текущий и следующий год */}
					<CustomTextField select required value={year} onChange={(e) => setYear(+e.target.value)}>
						{[new Date().getFullYear(), new Date().getFullYear() + 1].map((year) => (
							<CustomMenuItem key={year} value={year}>
								{year}
							</CustomMenuItem>
						))}
					</CustomTextField>
					{/* сохранение */}
					{isHaveRights && !isDisabled && (
						<CustomButton disabled={!changeFlg} onClick={handleSaveChanges}>
							{translate("button_save")}
						</CustomButton>
					)}
				</div>

				<div className={styles.calendar}>
					{MONTHS_LIST.map(({ month, translationFull }) => (
						<div className={styles.month} key={month}>
							<h3 className={styles.monthHeader}>{translate(translationFull)}</h3>
							<table className={styles.monthTable}>
								<thead>
									<tr>
										{WEEK_DAYS_LIST.map(({ day, translationShort }) => (
											<th
												key={day}
												className={cn({
													[styles.holiday]: day === 6 || day === 7,
												})}
											>
												{translate(translationShort)}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{createMonthArray(year, month).map((tr) => (
										<tr key={tr.join(",")}>
											{tr.map(([day, date], idx) => (
												<td
													key={`${date}${idx}`}
													data-value={day ? day : undefined}
													onClick={() => isHaveRights && clickHandler(date)}
													className={cn({
														[styles.currentDay]: date === getCurrentDate(),
														[styles.holiday]: holidays.includes(date),
													})}
												>
													{day}
												</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					))}
				</div>
			</div>
		</Fade>
	);
};

export default Calendar;
